import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAdblocks } from "@/lib/api";
import { CATEGORY_COLOR, categorySlug, categoryFromSlug } from "@/lib/readoo";

const AdContext = createContext({ blocks: [], consentGranted: null });
const ALL_CATEGORIES = Object.keys(CATEGORY_COLOR);

// Ritarda il caricamento (GA/GTM/Taboola) finché la pagina non è caricata + idle,
// oppure alla prima interazione, con timeout massimo. Abbatte TBT/TTI.
function useDeferredReady(maxDelay = 2500) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let done = false;
    const events = ["scroll", "pointerdown", "keydown", "touchstart", "mousemove"];
    const go = () => {
      if (done) return;
      done = true;
      events.forEach((e) => window.removeEventListener(e, go));
      setReady(true);
    };
    events.forEach((e) => window.addEventListener(e, go, { passive: true, once: true }));
    const schedule = () => {
      const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
      ric(() => setTimeout(go, 0), { timeout: maxDelay });
      setTimeout(go, maxDelay);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => events.forEach((e) => window.removeEventListener(e, go));
  }, [maxDelay]);
  return ready;
}

// Consenso via IAB TCF (__tcfapi della CMP). null=indeterminato, true=concesso, false=negato.
// FAIL-CLOSED: se la CMP non compare mai, resta null/false → i blocchi consent-gated NON si caricano.
function useConsent() {
  const [granted, setGranted] = useState(null);
  useEffect(() => {
    let active = true;
    let poll;
    const evaluate = (tcData, success) => {
      if (!active || !success || !tcData) return;
      if (tcData.gdprApplies === false) { setGranted(true); return; }
      const status = tcData.eventStatus;
      if (status === "tcloaded" || status === "useractioncomplete") {
        const p1 = tcData.purpose && tcData.purpose.consents && tcData.purpose.consents[1] === true;
        setGranted(p1 === true ? true : false);
      }
    };
    const attach = () => {
      if (typeof window.__tcfapi !== "function") return false;
      try { window.__tcfapi("addEventListener", 2, evaluate); } catch (e) { return false; }
      return true;
    };
    if (!attach()) {
      let tries = 0;
      poll = setInterval(() => {
        tries += 1;
        if (attach() || tries > 60) clearInterval(poll); // ~18s max
      }, 300);
    }
    return () => { active = false; if (poll) clearInterval(poll); };
  }, []);
  return granted;
}

export function AdProvider({ children }) {
  const [blocks, setBlocks] = useState([]);
  const ready = useDeferredReady();
  const consentGranted = useConsent();
  useEffect(() => {
    if (!ready) return;
    getAdblocks().then((data) => {
      const items = (data.items || []).slice().sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
      setBlocks(items);
    }).catch(() => {});
  }, [ready]);
  return <AdContext.Provider value={{ blocks, consentGranted }}>{children}</AdContext.Provider>;
}

export const useAds = () => useContext(AdContext);

// Un blocco è visibile se non richiede consenso, oppure se il consenso è concesso.
export function consentOk(block, consentGranted) {
  return !block.require_consent || consentGranted === true;
}

// Percorsi "asset"/spazzatura dove NON deve mai girare nulla (immagini, css, vecchi URL WP).
const ASSET_RE = /\.(jpe?g|png|gif|webp|avif|svg|ico|bmp|tiff?|mp4|webm|mp3|pdf|css|js|json|xml|txt|woff2?|ttf|eot|map)$/i;
export function isNoTrackPath(p = "") {
  return (
    p.startsWith("/wp-content") ||
    p.startsWith("/wp-includes") ||
    p.startsWith("/wp-admin") ||
    p.startsWith("/wp-json") ||
    p.startsWith("/reimposta-password") ||
    p.startsWith("/password-dimenticata") ||
    ASSET_RE.test(p)
  );
}

// Pagine "profilo" = tutta l'area utente (account, gamification).
const PROFILE_PATHS = [
  "/profilo", "/livelli", "/missioni", "/premi", "/il-mio-segno",
  "/quiz", "/storico-punti", "/riscatti", "/regole",
];

// Zona corrente in base al path: "admin" | "profilo" | "frontend".
export function zoneFromPath(p = "") {
  if (p.startsWith("/admin")) return "admin";
  if (PROFILE_PATHS.some((k) => p.startsWith(k))) return "profilo";
  return "frontend";
}

// Un blocco è ammesso nella zona corrente? Retrocompatibile: blocchi senza "zones"
// valgono solo per il frontend (comportamento storico).
export function zoneAllowed(block, zone) {
  const z = block.zones || { frontend: true, profilo: false, admin: false };
  return !!z[zone];
}

export function matchTarget(block, ctx = {}) {
  const tg = block.targeting || "all";
  if (tg === "all") return true;
  if (tg === "articles") return ctx.page === "article";
  if (tg === "home") return ctx.page === "home";
  if (tg === "category") return !!ctx.category && ctx.category === block.category;
  return false;
}

// Inserisce HTML eseguendo eventuali <script> (necessario per adv/GA).
// Ogni script inline è incapsulato in try/catch: un blocco difettoso non deve rompere il sito.
function fillWithExecutableHtml(container, html) {
  container.innerHTML = "";
  const tpl = document.createElement("template");
  tpl.innerHTML = html || "";
  Array.from(tpl.content.childNodes).forEach((node) => {
    try {
      if (node.nodeName === "SCRIPT") {
        const s = document.createElement("script");
        for (const attr of node.attributes) s.setAttribute(attr.name, attr.value);
        const hasSrc = node.hasAttribute("src");
        if (!hasSrc && node.textContent) {
          s.textContent = `try{\n${node.textContent}\n}catch(e){console.warn("[AdInserter] blocco ignorato:", e && e.message ? e.message : e);}`;
        } else {
          s.textContent = node.textContent;
        }
        container.appendChild(s);
      } else {
        container.appendChild(node.cloneNode(true));
      }
    } catch (e) {
      console.warn("[AdInserter] errore blocco:", e?.message || e);
    }
  });
}

// Render inline di un singolo blocco (per placement in-content), con etichetta opzionale.
export function AdRender({ block }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) fillWithExecutableHtml(ref.current, block.code);
  }, [block.code]);
  return (
    <div className="my-6 mx-auto text-center overflow-hidden" data-testid={`ad-block-${block.id}`}>
      {block.show_label && (
        <p className="text-[10px] uppercase tracking-[0.2em] text-subtle mb-1">Pubblicità</p>
      )}
      <div ref={ref} />
    </div>
  );
}

// Slot per un placement in-content: rende tutti i blocchi di quel placement filtrati per zona+target+consenso.
export function AdSlot({ placement, ctx, className }) {
  const { blocks, consentGranted } = useAds();
  const location = useLocation();
  if (isNoTrackPath(location.pathname)) return null;
  const zone = zoneFromPath(location.pathname);
  const items = blocks.filter((b) => b.placement === placement && zoneAllowed(b, zone) && matchTarget(b, ctx) && consentOk(b, consentGranted));
  if (!items.length) return null;
  return (
    <div className={className} data-testid={`ad-slot-${placement}`}>
      {items.map((b) => <AdRender key={b.id} block={b} />)}
    </div>
  );
}

// Inietta i blocchi "head" e "footer" nel documento. Split in due effetti:
// non-consent subito; consent-gated solo dopo consenso (così cambiare il consenso non re-inietta GA/CMP).
export function GlobalAds() {
  const { blocks, consentGranted } = useAds();
  const location = useLocation();
  const isNoTrack = isNoTrackPath(location.pathname);
  const zone = zoneFromPath(location.pathname);
  const injectGroup = (list) => {
    const created = [];
    list.forEach((b) => {
      const target = b.placement === "head" ? document.head : document.body;
      const wrap = document.createElement("div");
      wrap.setAttribute("data-adblock", b.id);
      wrap.style.display = "contents";
      fillWithExecutableHtml(wrap, b.code);
      target.appendChild(wrap);
      created.push(wrap);
    });
    return created;
  };
  useEffect(() => {
    if (isNoTrack) return;
    const list = blocks.filter((b) => (b.placement === "head" || b.placement === "footer") && zoneAllowed(b, zone) && !b.require_consent);
    const created = injectGroup(list);
    return () => created.forEach((w) => w.remove());
  }, [blocks, isNoTrack, zone]);
  useEffect(() => {
    if (isNoTrack || consentGranted !== true) return;
    const list = blocks.filter((b) => (b.placement === "head" || b.placement === "footer") && zoneAllowed(b, zone) && b.require_consent);
    const created = injectGroup(list);
    return () => created.forEach((w) => w.remove());
  }, [blocks, isNoTrack, zone, consentGranted]);
  return null;
}

export function pageFromPath(p) {
  if (p === "/") return "home";
  if (p.startsWith("/categoria/")) return "category";
  const known = ["/oroscopo", "/il-mio-segno", "/login", "/registrati", "/profilo",
    "/premi", "/livelli", "/quiz", "/storico-punti", "/riscatti", "/regole", "/missioni", "/admin"];
  if (known.some((k) => p.startsWith(k))) return "other";
  return "article";
}

// Categoria (nome canonico) dedotta dal path /categoria/:slug, per il targeting per categoria.
export function categoryFromPath(p) {
  const m = p.match(/^\/categoria\/([^/]+)/);
  if (!m) return null;
  return categoryFromSlug(m[1], ALL_CATEGORIES);
}

// Inietta i blocchi ancorati a un selettore CSS (prima/dentro/dopo). Riprova con timeout perché
// il DOM del contenuto può caricarsi dopo. Sortati per priority (dal sort in AdProvider).
export function SelectorAds() {
  const { blocks, consentGranted } = useAds();
  const location = useLocation();
  useEffect(() => {
    if (isNoTrackPath(location.pathname)) return;
    const zone = zoneFromPath(location.pathname);
    const ctx = { page: pageFromPath(location.pathname), category: categoryFromPath(location.pathname) };
    const sels = blocks.filter(
      (b) => ["before_html_element", "inside_html_element", "after_html_element"].includes(b.placement) && b.selector && zoneAllowed(b, zone) && matchTarget(b, ctx) && consentOk(b, consentGranted)
    );
    const created = [];
    const done = new Set();
    const tryInject = () => {
      sels.forEach((b) => {
        if (done.has(b.id)) return;
        let el;
        try { el = document.querySelector(b.selector); } catch { done.add(b.id); return; }
        if (!el || !el.parentNode) return;
        const wrap = document.createElement("div");
        wrap.setAttribute("data-adblock", b.id);
        if (b.show_label) {
          const lbl = document.createElement("p");
          lbl.textContent = "Pubblicità";
          lbl.className = "text-[10px] uppercase tracking-[0.2em] text-subtle mb-1";
          wrap.appendChild(lbl);
        }
        const codeBox = document.createElement("div");
        fillWithExecutableHtml(codeBox, b.code);
        wrap.appendChild(codeBox);
        if (b.placement === "inside_html_element") el.appendChild(wrap);
        else if (b.placement === "before_html_element") el.parentNode.insertBefore(wrap, el);
        else el.parentNode.insertBefore(wrap, el.nextSibling);
        created.push(wrap);
        done.add(b.id);
      });
    };
    const timers = [200, 600, 1200, 2200].map((d) => setTimeout(tryInject, d));
    return () => { timers.forEach(clearTimeout); created.forEach((w) => w.remove()); };
  }, [blocks, location.pathname, consentGranted]);
  return null;
}

export { categorySlug };
